import { makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { useMemo, useState } from "react";
import { useDataTableStyles } from "@/components/data-table";
import { FuiButton } from "@/components/fui-control";
import { AreaStackVisual, PhaseLanesVisual, TrendLineVisual } from "@/components/visual/charts";
import { SERIES } from "@/components/visual/chart-colors";
import { deltaSense, Metric, MetricPanel } from "@/components/metric-panel";
import { ReportVisual } from "@/components/visual/ReportVisual";
import { formatDate } from "@/lib/date-format";
import { fmtSpi } from "@/lib/format";
import { appGo } from "@/app/navigate";
import { openScopeInSchedule } from "@/features/tasks/links";
import { useProjectStore } from "@/store/project-store";
import { CostEvmStrip } from "@/features/pm/CostEvmStrip";
import { driverTable, finishDrivers, fmtWorkdays, phaseLanes, planCurve, planSummary, planTable, remainingForecast, remainingSummary, remainingTable, remainingTotal } from "./forecast-model";
import { SegmentedControl } from "@/features/resources/SegmentedControl";
import { CURRENT_KEY, useReportPoints, useReportStyles } from "./report-ui";

const DRIVER_LIMIT = 6;

const useStyles = makeStyles({
  empty: {
    margin: 0,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground3,
  },
  help: { margin: 0 },
  table: { width: "100%", tableLayout: "fixed" },
  nameCell: { width: "36%", overflow: "hidden" },
  clip: { display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 },
  row: {
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":active": { backgroundColor: tokens.colorNeutralBackground1Pressed },
  },
  more: { display: "flex", justifyContent: "flex-end", paddingTop: tokens.spacingVerticalXXS },
  cost: { paddingTop: tokens.spacingVerticalS },
});

function openOnSchedule(projectId: string, date: string) {
  appGo(`/projects/${projectId}/schedule`);
  requestAnimationFrame(() => {
    const state = useProjectStore.getState();
    if (state.activeId !== projectId) return;
    state.scrollToDate(date, "third");
  });
}

function workdayLabel(value: number): string {
  return `${fmtWorkdays(value)}일`;
}

export function ReportForecastView() {
  const s = useReportStyles();
  const st = useStyles();
  const ts = useDataTableStyles();
  const points = useReportPoints();
  const activeId = useProjectStore((state) => state.activeId);
  const [driversOpen, setDriversOpen] = useState(false);
  const [remainView, setRemainView] = useState<"stack" | "lanes">("lanes");
  const ctx = points.ctx(CURRENT_KEY)!;
  const forecast = useMemo(() => remainingForecast(ctx.project, ctx.schedule, ctx.calendar), [ctx]);
  const curve = useMemo(() => planCurve(ctx.evm), [ctx]);
  const drivers = useMemo(() => finishDrivers(ctx.project, ctx.schedule, ctx.baseline, ctx.calendar), [ctx]);
  const openDate = activeId ? (date: string) => openOnSchedule(activeId, date) : undefined;
  const openTask = activeId ? (taskId: string) => openScopeInSchedule(activeId, taskId) : undefined;
  const hasRemaining = forecast.bands.length > 0;
  const lanes = phaseLanes(forecast, ctx.baseline?.projectFinish ?? null, openDate);
  const driversView = driverTable(drivers, openTask);
  const shownDrivers = driversOpen ? driversView.rows : driversView.rows.slice(0, DRIVER_LIMIT);
  const nameIndex = driversView.columns.findIndex((column) => column.key === "name");
  const earnedY = curve?.earned[curve.statusIndex];
  const plannedY = curve?.planned[curve.statusIndex];
  const earned = earnedY != null ? Math.round(earnedY) : ctx.metrics.progress;
  const planned = plannedY != null ? Math.round(plannedY) : null;
  const remTotal = hasRemaining ? remainingTotal(forecast, 0) : 0;
  const finishSlip = ctx.metrics.finishVarianceDays;
  const spi = ctx.metrics.spi;

  return (
    <div className={s.wrap}>
      <div className={s.card}>
        <MetricPanel columns={4} ariaLabel="전망 핵심 지표">
          <Metric
            compact
            label="남은 공수"
            value={fmtWorkdays(remTotal)}
            unit="일"
            caption={hasRemaining ? `${forecast.bands.length}개 단계 잔여` : "잔여 작업 없음"}
            captionInline
            ariaLabel={`남은 공수 ${fmtWorkdays(remTotal)}일. ${hasRemaining ? `${forecast.bands.length}개 단계 잔여` : "잔여 작업 없음"}`}
          />
          <Metric
            compact
            label="기준일 누적 실적"
            value={earned}
            unit="%"
            delta={spi != null ? { text: `SPI ${fmtSpi(spi)}`, sense: deltaSense(spi - 1, true), title: "계획 대비" } : undefined}
            caption={planned != null ? `계획 ${planned}%` : "기준선 미저장"}
            captionInline
            ariaLabel={`기준일 누적 실적 ${earned}%${planned != null ? `. 계획 ${planned}%` : ""}${spi != null ? `. SPI ${fmtSpi(spi)}` : ""}`}
          />
          <Metric
            compact
            label="예상 종료일"
            value={formatDate(ctx.metrics.projectFinish, "short")}
            tone={ctx.metrics.hasBaseline && finishSlip > 5 ? "danger" : ctx.metrics.hasBaseline && finishSlip > 0 ? "warning" : undefined}
            delta={ctx.metrics.hasBaseline && finishSlip !== 0 ? { text: `${finishSlip > 0 ? "+" : ""}${finishSlip}일`, sense: deltaSense(finishSlip, false), title: "기준선 대비" } : undefined}
            caption={!ctx.metrics.hasBaseline ? "기준선 미저장" : finishSlip === 0 ? "기준선과 동일" : undefined}
            captionInline
            ariaLabel={`예상 종료일 ${formatDate(ctx.metrics.projectFinish, "full")}${!ctx.metrics.hasBaseline ? ". 기준선 미저장" : finishSlip === 0 ? ". 기준선과 동일" : `. 기준선 대비 ${finishSlip > 0 ? "+" : ""}${finishSlip}일`}`}
          />
          <Metric
            compact
            label="핵심경로 잔여 작업"
            value={drivers.length}
            unit="개"
            caption={drivers.length > 0 ? "여유 0일 · 지연 시 종료일 이동" : "여유 없는 잔여 작업 없음"}
            captionInline
            ariaLabel={`핵심경로 잔여 작업 ${drivers.length}개. ${drivers.length > 0 ? "여유 0일. 지연 시 종료일 이동" : "여유 없는 잔여 작업 없음"}`}
          />
        </MetricPanel>
        <div className={st.cost}>
          <CostEvmStrip project={ctx.project} evm={ctx.evm} earnedPercent={ctx.metrics.progress} />
        </div>
      </div>
      <ReportVisual
        title="남은 작업"
        description={hasRemaining ? (remainView === "lanes" ? lanes.headline : remainingSummary(forecast)) : undefined}
        help={
          <p className={st.help}>
            {remainView === "lanes"
              ? "막대는 기준일부터 그 단계가 비는 날까지입니다. 기준선보다 뒤는 위험색이고, 막대를 누르면 일정에서 그 날을 엽니다."
              : "단계는 일정의 순서이고, 아래 띠일수록 앞 단계입니다. 이미 끝난 작업은 빠집니다."}
          </p>
        }
        height={240}
        focusHeight={360}
        table={hasRemaining ? (remainView === "lanes" ? lanes.table : remainingTable(forecast, openDate)) : undefined}
        extra={
          hasRemaining ? (
            <SegmentedControl
              ariaLabel="남은 작업 보기"
              value={remainView}
              onChange={setRemainView}
              options={[
                { value: "lanes", label: "단계" },
                { value: "stack", label: "쌓기" },
              ]}
            />
          ) : undefined
        }
        chart={
          hasRemaining
            ? (height) =>
                remainView === "lanes" ? (
                  <PhaseLanesVisual lanes={lanes.lanes} referenceAt={lanes.referenceAt} ticks={lanes.ticks} height={height} ariaLabel={lanes.ariaLabel} />
                ) : (
                  <AreaStackVisual
                    labels={forecast.samples.map((sample) => sample.label)}
                    bands={forecast.bands}
                    markers={forecast.markers}
                    height={height}
                    ariaLabel={remainingSummary(forecast)}
                    formatValue={workdayLabel}
                    onPick={openDate ? (index) => openDate(forecast.samples[index]!.date) : undefined}
                  />
                )
            : undefined
        }
      >
        {!hasRemaining && <p className={st.empty}>남은 작업이 없어요.</p>}
      </ReportVisual>

      <ReportVisual
        title="계획 누적"
        description={curve ? planSummary(curve) : undefined}
        height={200}
        focusHeight={320}
        table={curve ? planTable(curve, openDate) : undefined}
        chart={
          curve
            ? (height) => (
                <TrendLineVisual
                  labels={curve.labels}
                  calloutTitles={curve.titles}
                  series={[
                    { key: "plan", legend: "계획", color: SERIES.plan, dashed: true, values: curve.planned, valueLabels: curve.planned.map((value) => `${Math.round(value)}%`) },
                    { key: "earned", legend: "실적 누적", color: SERIES.actual, values: curve.actualLine, valueLabels: curve.actualLine.map((value) => (value === null ? undefined : `${Math.round(value)}%`)) },
                    { key: "projected", legend: "예상 추세", color: SERIES.warn, dashed: true, values: curve.projectedLine, valueLabels: curve.projectedLine.map((value) => (value === null ? undefined : `${Math.round(value)}%`)) },
                  ]}
                  height={height}
                  yMin={0}
                  yMax={100}
                  yTicks={[0, 25, 50, 75, 100]}
                  formatY={(value) => `${Math.round(value)}%`}
                  ariaLabel={planSummary(curve)}
                  guide={curve && earnedY != null && plannedY != null ? { index: curve.statusIndex, fromY: plannedY, toY: earnedY } : null}
                  onPointClick={openDate ? (index) => openDate(curve.dates[index]!) : undefined}
                />
              )
            : undefined
        }
      >
        {!curve && <p className={st.empty}>계획을 저장하면 누적 곡선이 보여요.</p>}
      </ReportVisual>

      <ReportVisual title="종료일을 움직이는 작업" description="하루 늦으면 플랜 종료일도 하루 늦습니다.">
        {drivers.length === 0 ? (
          <p className={st.empty}>여유 없는 남은 작업이 없어요.</p>
        ) : (
          <>
            <div className={ts.frame}>
              <table className={mergeClasses(ts.table, st.table)} aria-label={driversView.ariaLabel}>
                <thead>
                  <tr>
                    {driversView.columns.map((column) => (
                      <th key={column.key} scope="col" className={mergeClasses(ts.th, column.numeric && ts.num, column.key === "name" && st.nameCell)}>
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {shownDrivers.map((row) => (
                    <tr key={row.key} className={row.onActivate ? st.row : undefined} onClick={row.onActivate}>
                      {driversView.columns.map((column, index) => {
                        const value = row.cells[index] ?? "—";
                        const name = index === nameIndex;
                        return (
                          <td key={column.key} className={mergeClasses(ts.td, column.numeric && ts.num, name && st.nameCell)} title={name ? String(value) : undefined}>
                            {index === 0 && row.onActivate ? (
                              <button
                                type="button"
                                className={ts.linkBtn}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  row.onActivate?.();
                                }}
                                aria-label={row.activateLabel ? `${value} · ${row.activateLabel}` : String(value)}
                              >
                                {value}
                              </button>
                            ) : (
                              <span className={name ? st.clip : undefined}>{value}</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {driversView.rows.length > DRIVER_LIMIT && (
              <div className={st.more}>
                <FuiButton appearance="subtle" onClick={() => setDriversOpen((open) => !open)}>
                  {driversOpen ? "접기" : `${driversView.rows.length - DRIVER_LIMIT}개 더 보기`}
                </FuiButton>
              </div>
            )}
          </>
        )}
      </ReportVisual>
    </div>
  );
}
